import { useMemo } from "react";
import { useDemoLive } from "../demo/demo-live-provider";

function fmtAgo(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const ORDER: Record<string, number> = { red: 0, amber: 1, green: 2 };

import type { DemoCallDoc } from "../demo/demo-live-provider";

export function AlertQueueV2({
  calls: callsProp,
  onSelectCall,
}: {
  calls?: DemoCallDoc[];
  onSelectCall: (id: string) => void;
}) {
  const { calls: demoCallsMap } = useDemoLive();

  const sorted = useMemo(() => {
    const list = callsProp || Object.values(demoCallsMap);
    return [...list].sort((a, b) => {
      const d = (ORDER[a.risk_tier] ?? 2) - (ORDER[b.risk_tier] ?? 2);
      if (d !== 0) return d;
      if (a.risk_tier !== "green") return (b.risk_score || 0) - (a.risk_score || 0);
      return new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
    });
  }, [callsProp, demoCallsMap]);

  const now = Date.now();

  return (
    <div className="h-full overflow-y-auto hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-testid="queue-page">
      <table className="w-full text-[11px] font-mono">
        <thead className="sticky top-0 bg-card border-b border-border z-10">
          <tr className="text-left text-[9px] tracking-widest text-muted-foreground uppercase">
            <th className="px-3 py-2">TIER</th>
            <th className="px-3 py-2">SCORE</th>
            <th className="px-3 py-2">ELAPSED</th>
            <th className="px-3 py-2">CUSTOMER</th>
            <th className="px-3 py-2">CAMPAIGN</th>
            <th className="px-3 py-2">LANG</th>
            <th className="px-3 py-2">STATE</th>
            <th className="px-3 py-2">TOP TRIGGER</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const elapsed = now - new Date(c.started_at).getTime();
            const lastReason = c.risk_reasons?.[c.risk_reasons.length - 1];

            return (
              <tr
                key={c.id}
                data-testid={`queue-row-${c.id}`}
                onClick={() => onSelectCall(c.id)}
                className={`border-b border-border cursor-pointer transition-colors ${
                  c.risk_tier === "red"
                    ? "bg-risk-red/10 hover:bg-risk-red/20"
                    : c.risk_tier === "amber"
                    ? "bg-risk-amber/5 hover:bg-risk-amber/15"
                    : "hover:bg-muted/40"
                }`}
              >
                <td className="px-3 py-2">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      c.risk_tier === "red"
                        ? "bg-risk-red"
                        : c.risk_tier === "amber"
                        ? "bg-risk-amber"
                        : "bg-risk-green"
                    }`}
                  />
                  <span
                    className={`ml-2 font-bold ${
                      c.risk_tier === "red"
                        ? "text-risk-red"
                        : c.risk_tier === "amber"
                        ? "text-risk-amber"
                        : "text-muted-foreground"
                    }`}
                  >
                    {c.risk_tier.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 tnum font-semibold">{c.risk_score}</td>
                <td className="px-3 py-2 tnum text-muted-foreground">{fmtAgo(elapsed)}</td>
                <td className="px-3 py-2 font-medium">{c.customer_ref || c.customer_name}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.campaign_name || "General"}</td>
                <td className="px-3 py-2 uppercase text-muted-foreground">{c.language}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.state.replace(/_/g, " ").toUpperCase()}</td>
                <td
                  className={`px-3 py-2 font-medium ${
                    c.risk_tier === "red"
                      ? "text-risk-red"
                      : c.risk_tier === "amber"
                      ? "text-risk-amber"
                      : "text-muted-foreground"
                  }`}
                >
                  {lastReason || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sorted.length === 0 && (
        <div className="py-20 text-center text-muted-foreground text-xs font-mono">NO LIVE CALLS</div>
      )}
    </div>
  );
}
